/** Modal dialogs: keyboard shortcuts ("?") and the autosave revision conflict. */
import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { X } from 'lucide-react';
import { t } from '../i18n.ts';
import { SHORTCUTS, displayKey } from '../shortcuts.ts';
import { useEditor, type EditorStore } from '../state/store.ts';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

/** The focusable elements inside a container, in tab order (visible ones only). */
export function focusables(root: HTMLElement | null): HTMLElement[] {
  return [...(root?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter((e) => e.getClientRects().length > 0);
}

/**
 * Dialog focus handling: on open, focus moves into the dialog (its first control, or the dialog itself); on close
 * it returns to where it was. The returned key handler keeps Tab and Shift+Tab inside the dialog.
 */
export function useDialogFocus(ref: RefObject<HTMLElement | null>, opts: { focusFirst?: boolean } = {}): (e: ReactKeyboardEvent) => void {
  const focusFirst = opts.focusFirst ?? true;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = focusFirst ? focusables(ref.current)[0] : undefined;
    (first ?? ref.current)?.focus();
    return () => {
      if (prev && prev.isConnected) prev.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (e) => {
    if (e.key !== 'Tab') return;
    const items = focusables(ref.current);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    const active = document.activeElement;
    const inside = ref.current?.contains(active) ?? false;
    if (e.shiftKey && (active === first || !inside || active === ref.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  };
}

export function Modal({ title, onClose, children, testId, labelledBy }: { title: string; onClose?: () => void; children: ReactNode; testId?: string; labelledBy: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const trap = useDialogFocus(ref);
  return (
    <div className="modal-backdrop" onPointerDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        ref={ref}
        data-testid={testId}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && onClose) {
            e.stopPropagation();
            onClose();
          }
          trap(e);
        }}
      >
        <div className="modal-head">
          <h2 id={labelledBy}>{title}</h2>
          {onClose && (
            <button type="button" className="icon-btn" aria-label={t('dialog.close')} onClick={onClose}>
              <X size={16} />
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

export function ShortcutsDialog({ store }: { store: EditorStore }) {
  const open = useEditor(store, (s) => s.ui.shortcutsOpen);
  if (!open) return null;
  const groups = ['general', 'create', 'arrange', 'view'] as const;
  return (
    <Modal title={t('shortcuts.title')} onClose={() => store.setUi({ shortcutsOpen: false })} testId="shortcuts-dialog" labelledBy="shortcuts-title">
      <div className="shortcut-groups">
        {groups.map((g) => (
          <section key={g}>
            <h3>{t(`shortcuts.group.${g}`)}</h3>
            <dl>
              {SHORTCUTS.filter((s) => s.group === g).map((s) => (
                <div key={s.label} className="shortcut">
                  <dt>{t(s.label)}</dt>
                  <dd>
                    {s.keys.map((k, i) => (
                      <kbd key={i}>{displayKey(k)}</kbd>
                    ))}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
      <p className="hint">{t('shortcuts.mouse')}</p>
    </Modal>
  );
}

export function ConflictDialog({ store }: { store: EditorStore }) {
  const save = useEditor(store, (s) => s.save);
  if (save.status !== 'conflict' || !save.conflict) return null;
  const when = save.conflict.updatedAt ? new Date(save.conflict.updatedAt).toLocaleString() : '';
  return (
    <Modal title={t('conflict.title')} testId="conflict-dialog" labelledBy="conflict-title">
      <p>{t('conflict.body', { revision: save.conflict.currentRevision, when })}</p>
      <div className="modal-actions">
        <button type="button" className="btn" onClick={() => void store.reload()} data-testid="conflict-reload">
          {t('conflict.reload')}
        </button>
        <button type="button" className="btn primary" onClick={() => void store.overwrite()} data-testid="conflict-overwrite">
          {t('conflict.overwrite')}
        </button>
      </div>
    </Modal>
  );
}
